/*
Description
    Thin API wrapper for Scheme Prices (Project 2116, Almondco Intake Masters
    Migration - #76526). Feature-scoped, matching the sibling
    almondco-grower-syndicate-split.service.js/almondco-grower-pool-split.service.js
    convention rather than folding into the general IntakeSettingService, since
    these calls are specific to this feature.

History
    01Sep2026 (SD)
        - #76605: 2116 - UI - Scheme Prices List and filter panel
        - #77291 follow-up: 2116 - UI - Scheme Prices List and filter panel,
          reworked to bind against api/AlmondcoSchemePriceSummary (#77290/
          #77291) instead of deriving the List's summary and the Scheme
          filter's option list client-side from the full raw
          api/AlmondcoSchemePrice row set (developer direction - the original
          AD-05 client-side approach does not scale as this table grows by
          thousands of rows/year). Added getSchemePriceSummary (RecordId
          lookup, mirrors AlmondcoGrowerSyndicateSplitService's own
          getSyndicateSplitSummary). Removed getSchemePrices (the full
          unfiltered raw GET) - no longer called from this screen; the raw
          api/AlmondcoSchemePrice endpoint itself is unchanged and still
          reached via getSchemePriceSet below.
    02Sep2026 (SD)
        - #76649: 2116 - UI - Add New Season/Scheme entry fields and START
          duplicate check. Added getSchemePriceSummaryByKey (same
          AlmondcoSchemePriceSummary endpoint as getSchemePriceSummary above,
          filtered on the exact SchemeId+SeasonYear just saved instead of a
          RecordId) - needed because a create-mode Save has no RecordId to
          route to afterwards until the server computes one; this is the
          scoped, single-row lookup that resolves it, same discipline as
          every other query on this screen (exact-key filter, never a full
          fetch).
        - Follow-up (developer-flagged scaling concern): the Scheme filter's
          distinct-value option list was itself still fetching
          AlmondcoSchemePriceSummary in full and deduplicating client-side -
          that endpoint grows with every Scheme+Season combination ever
          recorded, so it doesn't scale either. Removed
          getSchemePriceSummaryList (the unfiltered summary fetch) -
          scheme-prices.vue's Scheme filter now points GcDataGrid's own
          remote dataSourceConfig straight at api/AlmondcoSchemeSearch (a new,
          dedicated distinct-SchemeId endpoint) instead of going through this
          service file at all for that purpose.
        - #76635: 2116 - UI - Scheme Prices detail panel (Review/Edit grid).
          Added getVarieties (BR-06's Variety lookup - Inactive eq 'N' only;
          developer direction this session - the "non-packaging" half of
          BR-06 stays the already-documented open gap, technical-design.md
          §4/§11, no existing GCORPcx filter distinguishes packaging from
          non-packaging products), itemCodeDataSource (BR-06's Item Code
          lookup, always scoped to a required VarietyCode - same scaling
          discipline as api/AlmondcoSchemeSearch above, since
          AlmondcoIntakeVarietySizeProduct can hold many rows across every
          Variety), and saveSchemePrice (POST api/Save_AlmondcoSchemePrice,
          #76631's endpoint).
        - #76635 follow-up (developer-flagged scaling concern): itemCodeData
          Source's first version took no parameter and returned a single
          shared, Inactive-only-filtered DataSource meant to be re-filtered
          in place per row - but that instance had to exist (and therefore
          load) before any row had ever been edited, so its very first filter
          state was unscoped across every Variety. Now takes a required
          varietyCode and builds the filter with it from the start - there is
          no longer a code path that can request the unscoped set.

(c) Copyright Graycorp Pty Ltd (ABN 12 082 807 761). All Rights Reserved.
*/
import ApiService from "../../api.service";
import appConfig from "../../../../app-config";

ApiService.init(appConfig.VUE_APP_ROOT_API);

// Bug #77842: schemeId is free-typed by the user (Add New's Scheme field,
// Story 3) and was being dropped straight into an OData string literal with
// no escaping - a `'` breaks the literal itself, and URL-structural
// characters (&, %, =, #, +) broke EncodeODataQuery's own split-then-decode
// pass (requests.js), which runs on the raw, not-yet-encoded query string.
// Doubling the embedded quote (the standard OData string-literal escape) and
// percent-encoding the result before interpolation keeps both layers safe:
// EncodeODataQuery's decodeURIComponent round-trip correctly restores this
// exact text since it is valid percent-encoding, it just no longer contains
// any raw &/=/% for its naive split() to trip over.
const toODataFilterLiteral = (value) =>
  encodeURIComponent(String(value).replace(/'/g, "''"));

const AlmondcoSchemePriceService = {
  // §5: AlmondcoSchemePriceSummary, filtered to a single row's RecordId -
  // resolves the identity row (SchemeId/SeasonYear) on baseView.id change,
  // whether that's a row click or a direct/bookmarked URL. RecordId (not
  // SchemeId+SeasonYear) is the routable identity here - see
  // AlmondcoSchemePriceSummary.sql and scheme-prices.vue's own history note.
  async getSchemePriceSummary(recordId) {
    var url = `api/AlmondcoSchemePriceSummary?$filter=RecordId eq ${recordId}`;
    return await ApiService.get(url);
  },

  // #76649 (Add New): the same summary endpoint, filtered on the exact
  // SchemeId+SeasonYear pair instead of a RecordId. Two call sites in
  // scheme-prices.vue: onStartClicked (a scoped existence check on the
  // freshly-entered Season+Scheme before locking anything in - added
  // 2026-09-03, developer feedback, reversing an earlier "no check at all"
  // call) and onSaveClicked (resolves the new price set's own
  // server-computed RecordId right after a create-mode Save succeeds, since
  // there is none in the route yet to reuse the way Edit has - see its own
  // comment). Single-row lookup by exact composite key, same scaling
  // discipline as every other query here.
  async getSchemePriceSummaryByKey(schemeId, seasonYear) {
    var url = `api/AlmondcoSchemePriceSummary?$filter=SchemeId eq '${toODataFilterLiteral(schemeId)}' and SeasonYear eq ${seasonYear}`;
    return await ApiService.get(url);
  },

  // Loads a single existing Scheme+Season's rows - the detail panel's own
  // load call (Story 2's primary use; Story 1 reuses it only for the
  // Review-mode identity smoke check, see scheme-prices.vue's own comments).
  // No GetByID exists (AD-01) - $filter on the composite identity is the only
  // way to key one on, same shape as AlmondcoGrowerSplit/
  // AlmondcoGrowerSyndicateSplitSummary's own detail-load precedent.
  async getSchemePriceSet(schemeId, seasonYear) {
    var url = `api/AlmondcoSchemePrice?$filter=SchemeId eq '${toODataFilterLiteral(schemeId)}' and SeasonYear eq ${seasonYear}`;
    return await ApiService.get(url);
  },

  // Season option list (§6/BR-03) - Season/StartDate/EndDate from
  // AlmondcoIntakeSeason, "current FY" resolved the same way
  // console-season-year.vue's own setDefaultSeasonYear does (the row whose
  // date range contains today) - reuse-before-create, not a second
  // "current fiscal year" definition. Duplicated (not shared) from the sibling
  // grower-syndicate-splits.vue's own identical method, matching this
  // project's established one-service-file-per-screen convention.
  async getIntakeSeasons() {
    return await ApiService.get(
      "api/AlmondcoIntakeSeason?$select=Season,StartDate,EndDate&$orderby=Season desc",
    );
  },

  // §6/BR-06: Variety lookup for the detail grid's Variety column - active
  // only (Inactive eq 'N'). The "non-packaging" half of BR-06 stays the
  // already-documented open, non-blocking gap (technical-design.md §4/§11) -
  // no existing GCORPcx filter distinguishes packaging from non-packaging
  // products (confirmed against AlmondcoIntakeVarietySizeProductView.sql this
  // session). Small reference master (matches intake-variety.vue's own
  // table), safe to preload in full - unlike itemCodeDataSource below.
  async getVarieties() {
    return await ApiService.get(
      "api/AlmondcoIntakeVariety?$filter=Inactive eq 'N'&$orderby=Name",
    );
  },

  // §6/BR-06: Item Code lookup for the detail grid's Item Code column - a
  // fresh DataSource per call, always pre-filtered to the given VarietyCode
  // from the moment it's created (scheme-prices.vue's own onEditorPreparing
  // calls this once per row, right before that row's Item Code editor
  // opens - never cached/reused across rows). varietyCode is required and
  // never defaulted away - AlmondcoIntakeVarietySizeProduct can hold many
  // rows across every Variety, so there must be no code path that can ever
  // build this DataSource without the VarietyCode filter already applied;
  // that was exactly the bug in this method's first version (a shared,
  // column-level instance that had to start out unscoped), caught by the
  // developer this session and corrected here - see scheme-prices.vue's own
  // History block.
  itemCodeDataSource(varietyCode) {
    return ApiService.createDataSource("api/AlmondcoIntakeVarietySizeProduct", {
      filter: [
        ["VarietyCode", "=", varietyCode],
        "and",
        ["Inactive", "=", "N"],
      ],
      sort: [{ selector: "ProductDescription", asc: true }],
    });
  },

  // POST api/Save_AlmondcoSchemePrice (#76631) - full delete-then-insert save
  // of a Scheme+Season's price rows (BR-09).
  async saveSchemePrice(payload) {
    return await ApiService.post("api/Save_AlmondcoSchemePrice", payload);
  },
};

export { AlmondcoSchemePriceService };
